import {
  Box,
  Card,
  CardContent,
  Typography,
  Collapse,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  Divider,
  Alert,
} from '@mui/material'
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Help as HelpIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material'
import { useState } from 'react'

interface ConfidenceLevelProps {
  icon: React.ReactNode
  level: string
  range: string
  description: string
  examples: string[]
  color: 'success' | 'info' | 'warning' | 'error' | 'default'
}

let ConfidenceLevel = ({
  icon,
  level,
  range,
  description,
  examples,
  color,
}: ConfidenceLevelProps) => (
  <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 2 }}>
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        mb: 1,
      }}
    >
      <ListItemIcon sx={{ minWidth: 40 }}>{icon}</ListItemIcon>
      <Box>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 'bold',
          }}
        >
          <Chip
            label={`${level} ${range}`}
            size="small"
            sx={{
              color,
              mr: 1,
            }}
          />
        </Typography>
        <Typography color="text.secondary" variant="body2">
          {description}
        </Typography>
      </Box>
    </Box>
    <Box
      sx={{
        ml: 5,
      }}
    >
      <Typography
        color="text.secondary"
        variant="caption"
        sx={{
          fontWeight: 'bold',
        }}
      >
        Examples:
      </Typography>
      {examples.map((example, index) => (
        <Typography
          color="text.secondary"
          key={index}
          variant="caption"
          sx={{
            display: 'block',
          }}
        >
          • {example}
        </Typography>
      ))}
    </Box>
  </ListItem>
)

export default () => {
  let [expanded, setExpanded] = useState(false)

  let confidenceLevels: ConfidenceLevelProps[] = [
    {
      icon: <CheckCircleIcon color="success" />,
      level: 'Very High',
      range: '(95-100%)',
      description:
        'Post-session evidence: Course code matches exactly AND submission within 4 hours after session ends',
      examples: [
        "User attends '24-FOAM-F5-0449' session at 2 PM, submits survey at 4 PM with matching course code",
        'Quiz submitted 3 hours after session with exact course code match',
      ],
      color: 'success',
    },
    {
      icon: <InfoIcon color="info" />,
      level: 'High',
      range: '(85-94%)',
      description:
        'Post-session evidence: Course code matches AND submission within 24 hours after session, OR flexible course code match within 2 hours after session',
      examples: [
        'Survey with correct course code submitted 12 hours after session',
        'Quiz matching course pattern (ignoring year) submitted 1 hour after session',
      ],
      color: 'info',
    },
    {
      icon: <WarningIcon color="warning" />,
      level: 'Medium',
      range: '(60-84%)',
      description:
        'Post-session evidence: Course code matches with longer delay (24-72 hours after session), OR submission within 4-24 hours after session. Pre-session evidence: Course code matches exactly AND submission within 2 hours before session',
      examples: [
        'Survey submitted 2 days after session with matching course code',
        'Quiz submitted 1 hour before session with exact course code match',
      ],
      color: 'warning',
    },
    {
      icon: <ErrorIcon color="error" />,
      level: 'Low',
      range: '(30-59%)',
      description:
        'Post-session evidence: Submission within 1 week after session with weaker indicators. Pre-session evidence: Course code matches within 24 hours before session, OR flexible course code match within 1 hour before session',
      examples: [
        'Survey submitted 5 days after session with no course code match',
        'Quiz submitted 12 hours before session with matching course code',
      ],
      color: 'error',
    },
    {
      icon: <CancelIcon color="disabled" />,
      level: 'Uncertain',
      range: '(0-29%)',
      description:
        'Pre-session evidence: Weak course code matching or submissions more than 24 hours before session. Old evidence: Submissions more than 1 week from any session',
      examples: [
        'Survey submitted 2 days before session',
        'Quiz submitted 2 weeks after all sessions ended',
      ],
      color: 'default',
    },
  ]

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent sx={{ bgcolor: 'background.paperDark' }}>
        <Box
          onClick={() => setExpanded(!expanded)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <HelpIcon
              color="primary"
              sx={{
                mr: 1,
              }}
            />
            <Typography variant="h6">
              📊 Attendance Confidence Explained
            </Typography>
          </Box>
          <IconButton size="small">
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        <Collapse in={expanded}>
          <Box
            sx={{
              mt: 2,
            }}
          >
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Why confidence scores?</strong>
              </Typography>
              <Typography variant="body2">
                Live classes often have multiple sessions (e.g., Tuesday 10 AM,
                Thursday 3 PM). When users submit quizzes/surveys, we calculate
                confidence scores to determine which session they most likely
                attended based on timing and course code matching.
              </Typography>
            </Alert>

            <Typography
              variant="subtitle1"
              gutterBottom
              sx={{
                fontWeight: 'bold',
              }}
            >
              Confidence Levels:
            </Typography>

            <List>
              {confidenceLevels.map((level, index) => (
                <Box key={index}>
                  <ConfidenceLevel {...level} />
                  {index < confidenceLevels.length - 1 && <Divider />}
                </Box>
              ))}
            </List>

            <Alert severity="success" sx={{ mt: 2 }}>
              <Typography variant="body2" gutterBottom>
                <strong>For Accreditation:</strong>
              </Typography>
              <Typography variant="body2">
                Higher confidence scores provide stronger evidence for
                compliance reporting. Each user's evidence is assigned to their
                most likely attended session based on the highest confidence
                score.
              </Typography>
            </Alert>

            <Box
              sx={{
                bgcolor: 'action.hover',
                borderRadius: 1,
                mt: 2,
                p: 2,
              }}
            >
              <Typography color="text.primary" variant="caption">
                <strong>Key Logic:</strong> Post-session evidence (submitted
                after session time) receives higher confidence than pre-session
                evidence. Course code matching: Exact match preferred, but
                flexible matching on last 3 sections (e.g., '23-FOAM-F5-0449'
                matches '24-FOAM-F5-0449'). Temporal proximity: Closer
                submission times to session receive higher confidence. Global
                assignment: Each quiz/survey submission is assigned to only one
                session to prevent double-counting.
              </Typography>
            </Box>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  )
}
